import { useNavigate } from "react-router-dom";

import { Carousel, Col, Layout, Row, Spin, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

import StatCard from "@/components/Dashboard/Card";
import PawnTickets from "@/components/Dashboard/PawnTickets";
import Breadcrumbs from "@/components/common/Breadcrumb";
import { CURRENCY_SYMBOLS, IMAGES } from "@/constant";
import styles from "@/pages/Dashboard/Dashbaord.module.css";
import { getBannerList, getDashboardData } from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";
import { PawnTicket } from "@/types/authService";

const { Content } = Layout;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Just loop 3 banners
  const [loading, setLoading] = useState(false);
  const currency = useAuthStore((state) => state.currency);
  const currencyConfig = currency ? CURRENCY_SYMBOLS[currency] : null;
  const [pawnData, setPawnData] = useState<PawnTicket[]>();
  const [banners, setBanners] = useState<any[]>([]);
  const [tabData, setTabData] = useState<{
    active_ticket_count: number;
    total_due: number;
    total_interest_active: number;
    total: number;
    since: string;
    pledgeCountWithOutDraft: number;
    openPledgeCount: number;
  }>();

  const fetchDashboardData = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await getDashboardData({
        active: "true",
        is_applied_promotion_interest: "true",
        selected_txn_type: "renewal",
      });
      const {
        data: pawnTickets,
        error,
        notDraftPledgesCount,
        openPledgesCount,
        ...headerData
      } = data;
      setTabData({
        ...headerData,
        pledgeCountWithOutDraft: notDraftPledgesCount,
        openPledgeCount: openPledgesCount,
      });
      setPawnData(pawnTickets?.map((el, index) => ({ ...el, key: index })));
    } catch (error: unknown) {
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBannerList = async () => {
    const today = dayjs().format("YYYY-MM-DD");
    try {
      const response = await getBannerList({ startDate: today });
      setBanners(response?.data);
    } catch (error) {
      console.error("Error fetching banner list", error);
    }
  };

  useEffect(() => {
    void fetchDashboardData();
    void fetchBannerList();
  }, []);

  return (
    <Spin spinning={loading}>
      <Content className={styles.container}>
        <div className={styles.customBreadCrumbs}>
          <Breadcrumbs items={[{ label: "Dashboard", replayPrivacy: "allow" }]} />
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Total Interest"
              subTitle="Payable"
              bgImage={IMAGES.DASHBOARD_1st_card}
              value={`${currencyConfig?.note || ""} ${tabData?.total_interest_active ?? "00.00"}`}
              footer={`Till ${dayjs().format("DD MMM, YYYY")}`}
              icon={IMAGES.PAYABLE_ICON}
              color=""
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Pledge"
              subTitle="Tickets"
              bgImage={IMAGES.DASHBOARD_2nd_card}
              value={String(tabData?.pledgeCountWithOutDraft ?? 0)}
              footer={`Since ${dayjs(tabData?.since).format("DD MMM, YYYY")}`}
              icon={IMAGES.DUE_ICON}
              color=""
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Active Pledge"
              subTitle="Tickets"
              bgImage={IMAGES.DASHBOARD_3rd_card}
              value={String(tabData?.openPledgeCount ?? 0)}
              footer=""
              icon={IMAGES.ACTIVE_TICKET_ICON}
              color=""
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Total"
              subTitle="Due"
              bgImage={IMAGES.DASHBOARD_4th_card}
              value={`${currencyConfig?.note || ""} ${tabData?.total_due ?? "00.00"}`}
              icon={IMAGES.DUE_ICON}
              color="#fff"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} md={16}>
            <div className={styles.pawnTicketContainer}>
              <span className={styles.title}>Pawn Tickets</span>
              <span className={styles.seeAll} onClick={() => navigate("/pledge")}>
                VIEW ALL
              </span>
            </div>
            <div className={styles.customCol}>
              <PawnTickets pawnTickets={pawnData ?? []} />
            </div>
          </Col>

          <Col xs={24} md={8} className={styles.customCarousel}>
            <div className={styles.fullCarousel}>
              <Carousel autoplay dots className={styles.carouselWidth}>
                {banners.map((item, index) => (
                  <div key={index} className={styles.carouselWidth}>
                    <img src={item?.image_url} alt={`banner-${index}`} className={styles.banner} />
                  </div>
                ))}
              </Carousel>
            </div>
          </Col>
        </Row>
      </Content>
    </Spin>
  );
};

export default Dashboard;
